/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { TopBar } from '@/lib/components/TopBar/TopBar';

import { iframeInit as intercomInit, sendEvent } from './iframeIntercom';
import useMessage from './useMessage';

function showIframe(iframe: HTMLIFrameElement) {
  iframe.style.display = 'block';
}

function hideIframe(iframe: HTMLIFrameElement) {
  iframe.style.display = 'none';
}

let stateHandler: {
  status: 'connecting' | 'connected';
};

let lastSentTab: string | undefined;

function updateStateHandler(iframe: HTMLIFrameElement, tab: string) {
  if (!stateHandler) {
    stateHandler = {
      status: 'connecting',
    };
    // eslint-disable-next-line promise/catch-or-return
    (async () => {
      await intercomInit(iframe);
      stateHandler.status = 'connected';
      sendEvent('tabSwitch', { tab: lastSentTab || tab }, iframe);
      return;
    })().finally(() => {});
  }
  if (stateHandler.status === 'connected') {
    sendEvent('tabSwitch', { tab }, iframe);
  } else {
    lastSentTab = tab;
  }
}

function initIframe({ targetIframe, enabled }: { targetIframe: string; enabled: boolean }) {
  let iframe = document.getElementById('v1-container');
  if (!iframe) {
    const parent = document.createElement('div');
    parent.id = 'v1-container-parent';
    parent.style.position = 'relative';
    document.body.appendChild(parent);
    iframe = document.createElement('iframe');
    iframe.id = 'v1-container';
    iframe.setAttribute('src', targetIframe);
    iframe.setAttribute('border', 'none');
    iframe.style.position = 'absolute';
    iframe.style.left = '0px';
    iframe.style.right = '0px';
    iframe.style.top = '-64px';
    iframe.style.marginTop = '23px';
    iframe.style.bottom = '0px';
    iframe.style.width = '100%';
    iframe.style.height = 'calc(100vh - 23px)';
    if (!enabled) {
      iframe.style.display = 'none';
    }
    parent.appendChild(iframe);
  }
  return iframe as HTMLIFrameElement;
}

type IframeContainerProps = V1CompatHandlerProps;

function IframeContainer(props: IframeContainerProps) {
  const val = useRef<IframeContainerProps>();

  useEffect(() => {
    val.current = props;
  }, [props]);

  useEffect(() => {
    const iframe = initIframe({ targetIframe: props.targetIframe, enabled: props.enabled });
    props.enabled && showIframe(iframe);
    updateStateHandler(iframe, props.tab ?? '');
    return () => {
      if (iframe) {
        hideIframe(iframe);
      }
    };
  }, [val, props.tab, props.onTabUpdate, props.targetIframe, props.enabled]);

  useMessage('emblueCompat', (_, payload: { event: string; tab: string }) => {
    if (payload.event === 'tabSwitch') {
      props.onTabUpdate?.(payload.tab);
    }
  });

  return null;
}
interface V1CompatHandlerProps {
  tab?: string;
  enabled: boolean;
  targetIframe: string;
  onTabUpdate?: (tab: string) => void;
}
export const V1CompatHandler = ({
  enabled = false,
  tab,
  onTabUpdate,
  targetIframe,
}: V1CompatHandlerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  if (!tab) {
    tab = location.pathname;
  }
  const defaultTabUpdate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );
  return (
    <IframeContainer
      enabled={enabled}
      tab={tab}
      targetIframe={targetIframe}
      onTabUpdate={onTabUpdate || defaultTabUpdate}
    />
  );
};

export const V1CompatHandlerTopBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div style={{ position: 'relative' }}>
      <TopBar email="jdoe@embluemail.com" name="John Doe" />
      <V1CompatHandler
        enabled
        tab={location.pathname}
        targetIframe={
          'https://apps.embluemail.com/r/ema-app-new-ui/4563e3b070120294bc00b1836ea26113eb9f3585/tabs-demo.html'
        }
        onTabUpdate={(tabValue: string) => {
          navigate(tabValue);
        }}
      />
    </div>
  );
};
